import * as React from 'react';

import {
    AccountProviderType,
    accountIsInErrorState,
    getOWAConnectedAccountStatus,
    isOWAConnectedAccountValid,
} from 'owa-accounts-store';
import { Calendar, getCalendarGroupKey, isGroupCalendar } from 'owa-calendar-cache';
import CalendarPickerGroup, { CalendarPickerGroupHandle } from './CalendarPickerGroup';
import { DefaultButton, IButton, IconButton } from '@fluentui/react/lib/Button';
import { Icon, IconType } from '@fluentui/react/lib/Icon';
import { KeyCodes, getId } from '@fluentui/react/lib/Utilities';
import {
    accountErrorTooltip,
    ellipsisAccesibility,
    newGroupAccessibility,
    showAllCalendarsString,
    showSelectedCalendarsString,
} from '../strings.locstring.json';
import {
    condenseCalendarAccount,
    toggleCondenseCalendarAccount,
} from '../mutators/toggleCondenseCalendarAccountMutator';
import { expandAccount, toggleCalendarAccount } from '../mutators/collapseCalendarAccountMutators';
import {
    getCalendarGroups,
    getCalendarGroupsForUser,
    getSelectedCalendars,
    isSelectedCalendar,
} from 'owa-calendar-surface-calendars-store';
import getCalendarsForCalendarGroup, {
    GetCalendarsListType,
    getTopAndSelectedCalendars,
} from '../selectors/getCalendarsForCalendarGroup';
import {
    setScenarioCondensedViewCalendars,
    showCalendarContextMenu,
} from '../actions/calendarActions';
import {
    startedCreatingCalendarGroup,
    stoppedCreatingCalendarGroup,
} from '../mutators/creatingCalendarGroupMutators';

import CalendarAccountGroupContextMenu from './CalendarAccountGroupContextMenu';
import type { CalendarGroup } from 'owa-graph-schema';
import CalendarGroupType from 'owa-service/src/contract/CalendarGroupType';
import CalendarNodeTextField from './CalendarNodeTextField';
import CalendarPickerCalendar from './CalendarPickerCalendar';
import CalendarPickerDroppablePlaceholder from './CalendarPickerDroppablePlaceholder';
import CalendarPickerScenarioId from '../store/schema/CalendarPickerScenarioId';
import { ControlIcons } from 'owa-control-icons';
import { DraggableItemTypes } from 'owa-dnd';
import { GROUP_CALENDAR_GROUP_ID } from 'owa-calendarsapi-outlook-groups';
import { MAX_CALENDARS_FOR_TOP_CALENDARS_LIST } from '../utils/constants';
import type { MailboxType } from 'owa-client-ids';
import { createCalendarGroup } from '../actions/calendarGroupActions';
import getAnchorElementForContextMenu from '../utils/getAnchorElementForContextMenu';
import getContextMenuViewStateForItem from '../selectors/getContextMenuViewStateForItem';
import { getFlexContainerStyle } from './getFlexContainerStyle';
import getIsCreatingCalendarGroup from '../selectors/getIsCreatingCalendarGroup';
import getSelectedCalendarsForUser from '../selectors/getSelectedCalendarsForUser';
import { hideContextMenu } from '../mutators/contextMenuMutators';
import isAccountCollapsed from '../selectors/isAccountCollapsed';
import isAccountCondensed from '../selectors/isAccountCondensed';
import isCalendarGroupBeingDragged from '../selectors/isCalendarGroupBeingDragged';
import { isFeatureEnabled } from 'owa-feature-flags';
import loc from 'owa-localize';
import { logUsage } from 'owa-analytics';
import { observer } from 'mobx-react';

const styles = require('./CalendarPicker.scss');
const classNames = require('classnames/bind').bind(styles);

/**
 * The properties for the calendar account picker
 */
export interface CalendarAccountGroupProps {
    scenarioId: CalendarPickerScenarioId;
    userIdentity: string;
    accountProvider: AccountProviderType;
    hasMultipleAccounts: boolean;
    className: string;
    onClickAccountSettings?: () => void;
    onClickAddTeamsCalendar?: () => void;
}

interface CalendarPickerAccountState {
    contextMenuHasKeyboardFocus: boolean;
    contextMenuIsActive: boolean;
}

/**
 * The calendar account group component for rendering calendar group headers and the calendars they contain.
 */
@observer
export default class CalendarAccountGroup extends React.Component<
    CalendarAccountGroupProps,
    CalendarPickerAccountState
> {
    calendarPickerGroupRefs: CalendarPickerGroupHandle[] = [];
    buttonRef: IButton;
    contextMenuButtonRef: IButton;

    constructor(props: CalendarAccountGroupProps) {
        super(props);
        this.state = {
            contextMenuHasKeyboardFocus: false,
            contextMenuIsActive: false,
        };

        condenseCalendarAccount(this.props.scenarioId, this.props.userIdentity);
        // Set which calendars are visible when an account is condensed when a new scenario is introduced
        setScenarioCondensedViewCalendars(
            getSelectedCalendars(),
            this.props.scenarioId,
            this.props.userIdentity
        );
    }

    render() {
        const {
            scenarioId,
            userIdentity,
            accountProvider,
            hasMultipleAccounts,
            onClickAddTeamsCalendar,
        } = this.props;
        const {
            userCalendarGroups,
            groupCalendarGroups,
            teamsCalendarGroups,
        } = getCalendarGroupsForUser(userIdentity);
        const selectedCalendars = getSelectedCalendarsForUser(scenarioId, userIdentity);
        const isCollapsed = isAccountCollapsed(scenarioId, userIdentity);
        const isCondensed = isAccountCondensed(scenarioId, userIdentity);
        const buttonLabel = isCondensed
            ? loc(showAllCalendarsString)
            : loc(showSelectedCalendarsString);

        const isAccountValid = isOWAConnectedAccountValid(userIdentity);
        const calendarAccountGroupHeaderId = getId('calendarAccountGroupHeader');

        return hasMultipleAccounts ? (
            <div className={classNames(this.props.className, styles.groupCalendarGroup)}>
                {this.renderCalendarAccountGroupHeader(
                    userIdentity,
                    accountProvider,
                    isCollapsed,
                    isCondensed,
                    isAccountValid,
                    isCondensed && calendarAccountGroupHeaderId
                )}

                {!isCollapsed && isAccountValid && (
                    <div
                        className={this.props.className}
                        role={isCondensed ? 'listbox' : undefined}
                        aria-labelledby={isCondensed ? calendarAccountGroupHeaderId : undefined}>
                        {isCondensed
                            ? this.renderSelectedCalendars(selectedCalendars, isCondensed)
                            : this.renderAllCalendarGroups(
                                  userCalendarGroups,
                                  groupCalendarGroups,
                                  teamsCalendarGroups,
                                  scenarioId,
                                  userIdentity,
                                  isCondensed,
                                  onClickAddTeamsCalendar
                              )}
                        <DefaultButton
                            styles={getFlexContainerStyle()}
                            className={styles.calendarListDefaultButton}
                            title={buttonLabel}
                            onClick={this.onShowMoreOrLessButtonClickFromPickerMenu}>
                            <span className={styles.showMoreOrLessLabel}>{buttonLabel}</span>
                        </DefaultButton>
                    </div>
                )}
            </div>
        ) : (
            <div className={this.props.className}>
                {this.renderAllCalendarGroups(
                    userCalendarGroups,
                    groupCalendarGroups,
                    teamsCalendarGroups,
                    scenarioId,
                    userIdentity,
                    isCondensed,
                    onClickAddTeamsCalendar
                )}
            </div>
        );
    }

    private renderCalendarAccountGroupHeader(
        userIdentity: string,
        accountProvider: AccountProviderType,
        isCollapsed: boolean,
        isCondensed: boolean,
        accountValid: boolean,
        headerId: string
    ): JSX.Element {
        const contextMenuState = getContextMenuViewStateForItem(
            this.props.scenarioId,
            userIdentity
        );
        const collapseIcon = isCollapsed ? ControlIcons.ChevronRight : ControlIcons.ChevronDown;
        const onClickFunc = accountValid
            ? this.onCalendarGroupHeaderClick
            : () => showAccountLoadError(userIdentity, accountProvider);

        // Should be updated if we allow support for multiple accounts of the same provider
        const accountHeaderLabel = userIdentity;
        const onAccountSettings =
            accountProvider != null ? this.props.onClickAccountSettings : null;
        if (accountValid) {
            return (
                <div
                    className={classNames(styles.listItemContainer, styles.contextMenuEnabled, {
                        contextMenuFocused: this.state.contextMenuHasKeyboardFocus,
                        contextMenuActive: this.state.contextMenuIsActive,
                    })}>
                    <DefaultButton
                        id={headerId}
                        className={classNames(styles.calendarListHeaderButton, {
                            isMainHeader: this.props.hasMultipleAccounts,
                        })}
                        styles={getFlexContainerStyle()}
                        onClick={onClickFunc}
                        onKeyDown={this.accountHeaderOnKeyDown}
                        componentRef={this.setButtonRef}>
                        <div className={styles.accountHeaderIcon}>
                            <Icon className={'flipForRtl'} iconName={collapseIcon} />
                        </div>
                        <span className={styles.accountHeaderLabel}>{accountHeaderLabel}</span>
                        <CalendarAccountGroupContextMenu
                            contextMenuViewState={contextMenuState}
                            isCondensed={isCondensed}
                            onAccountSettings={onAccountSettings}
                            onDismiss={this.onContextMenuDismiss}
                            onShowMoreOrLess={this.onShowMoreOrLessButtonClickFromContextMenu}
                            onNewCalendarGroup={this.onNewCalendarGroup}
                        />
                    </DefaultButton>
                    <IconButton
                        className={styles.listHeaderContextMenuButton}
                        ariaLabel={loc(ellipsisAccesibility)}
                        tabIndex={-1}
                        iconProps={{ iconName: ControlIcons.More }}
                        onClick={this.onContextMenuClick}
                        componentRef={this.setContextMenuButtonRef}
                        onKeyDown={this.contextMenuOnKeyDown}
                        onFocus={this.contextMenuButtonOnFocus}
                        onBlur={this.contextMenuButtonOnBlur}
                    />
                </div>
            );
        } else {
            return (
                <div className={styles.listItemContainer}>
                    <DefaultButton
                        className={classNames(styles.calendarListHeaderButton, {
                            isMainHeader: this.props.hasMultipleAccounts,
                        })}
                        styles={getFlexContainerStyle()}
                        title={loc(accountErrorTooltip)}
                        onClick={onClickFunc}
                        onKeyDown={this.accountHeaderOnKeyDown}
                        componentRef={this.setButtonRef}>
                        <div className={styles.accountHeaderIcon}>
                            <Icon
                                iconName={ControlIcons.Error}
                                iconType={IconType.Default}
                                className={styles.errorIcon}
                            />
                        </div>
                        <span className={styles.accountHeaderLabel}>{userIdentity}</span>
                    </DefaultButton>
                </div>
            );
        }
    }

    private renderAllCalendarGroups(
        userCalendarGroups: CalendarGroup[],
        groupCalendarGroups: CalendarGroup[],
        teamsCalendarGroups: CalendarGroup[],
        scenarioId: CalendarPickerScenarioId,
        userIdentity: string,
        isCondensed: boolean,
        onClickAddTeamsCalendar: () => void
    ): JSX.Element {
        const userGroups = this.renderCalendarGroups(
            userCalendarGroups,
            true,
            true,
            isCondensed,
            userIdentity,
            'UserMailbox'
        );
        const isCreatingCalendarGroup = getIsCreatingCalendarGroup(scenarioId, userIdentity);
        const groupGroups = this.renderCalendarGroups(
            groupCalendarGroups,
            true,
            false,
            isCondensed,
            userIdentity,
            'GroupMailbox',
            this.props.hasMultipleAccounts ? false : getShowMoreForGroupCalendars(userIdentity)
        );

        const teamGroups = this.renderCalendarGroups(
            teamsCalendarGroups,
            true,
            false,
            isCondensed,
            userIdentity,
            'TeamsMailbox',
            false,
            onClickAddTeamsCalendar
        );

        return (
            <ul>
                {isCreatingCalendarGroup
                    ? [...userGroups, this.renderNewCalendarGroup(), ...teamGroups, ...groupGroups]
                    : [...userGroups, ...teamGroups, ...groupGroups]}
            </ul>
        );
    }

    private renderSelectedCalendars(
        selectedCalendars: Calendar[],
        isCondensed: boolean
    ): JSX.Element[] {
        return selectedCalendars.map(selectedCalendar => {
            return (
                <CalendarPickerCalendar
                    key={selectedCalendar.id.id}
                    scenarioId={this.props.scenarioId}
                    selected={isSelectedCalendar(selectedCalendar)}
                    calendar={selectedCalendar}
                    showContextMenuButton={
                        this.props.scenarioId !== CalendarPickerScenarioId.AddCalendarModal &&
                        !isGroupCalendar(selectedCalendar)
                    }
                    hasMultipleAccounts={this.props.hasMultipleAccounts}
                    isCondensed={isCondensed}
                />
            );
        });
    }

    private renderCalendarGroups(
        calendarGroups: CalendarGroup[],
        showGroupContextMenuButton: boolean,
        showListItemContextMenuButton: boolean,
        isCondensed: boolean,
        userIdentity: string,
        mailboxType: MailboxType,
        showMoreForCalendarGroups?: boolean,
        onClickAddTeamsCalendar?: () => void
    ): JSX.Element[] {
        let prevGroupId: string;
        const components: JSX.Element[] = [];

        for (let i = 0; i < calendarGroups.length; i++) {
            const calendarGroupId = calendarGroups[i].calendarGroupId.id;
            const nextGroupId =
                i + 1 < calendarGroups.length ? calendarGroups[i + 1].serverGroupId : null;

            components.push(
                <CalendarPickerGroup
                    scenarioId={this.props.scenarioId}
                    className={classNames(styles.group, {
                        spacedPickerButton: !this.props.hasMultipleAccounts,
                    })}
                    key={calendarGroupId}
                    ref={ref => (this.calendarPickerGroupRefs[calendarGroupId] = ref)}
                    userIdentity={userIdentity}
                    hasMultipleAccounts={this.props.hasMultipleAccounts}
                    isCondensed={isCondensed}
                    calendarGroup={calendarGroups[i]}
                    onNewCalendarGroup={startedCreatingCalendarGroup}
                    onDeleteCalendarGroup={this.onDeleteCalendarGroup}
                    showMoreForCalendarGroup={showMoreForCalendarGroups}
                    showGroupContextMenuButton={showGroupContextMenuButton}
                    showListItemContextMenuButton={showListItemContextMenuButton}
                    onClickAddTeamsCalendar={onClickAddTeamsCalendar}
                    prevCalendarGroupId={prevGroupId}
                    nextCalendarGroupId={nextGroupId}
                />
            );
            prevGroupId = calendarGroups[i].serverGroupId;
        }

        if (
            isFeatureEnabled('cal-calendarPickerReorder') &&
            mailboxType === 'UserMailbox' &&
            isCalendarGroupBeingDragged(this.props.scenarioId)
        ) {
            components.push(
                <CalendarPickerDroppablePlaceholder
                    itemType={DraggableItemTypes.CalendarGroupNode}
                    previousItemId={prevGroupId}
                    userIdentity={userIdentity}
                    scenarioId={this.props.scenarioId}
                />
            );
        }

        return components;
    }

    /**
     * renders new group calendar textbox
     */
    private renderNewCalendarGroup(): JSX.Element {
        return (
            <div className={styles.calendarListItem}>
                <div
                    className={
                        this.props.hasMultipleAccounts
                            ? styles.calendarListSubHeaderIcon
                            : styles.calendarListHeaderIcon
                    }>
                    <Icon
                        iconName={ControlIcons.ChevronUp}
                        className={
                            this.props.hasMultipleAccounts
                                ? styles.newGroupSmallIcon
                                : styles.newGroupIcon
                        }
                    />
                </div>
                <CalendarNodeTextField
                    className={classNames(styles.textboxCalendarGroup, styles.group)}
                    onEntry={this.onNewCalendarGroupEntry}
                    onDismiss={this.onStoppedCreatingCalendarGroup}
                    ariaLabel={loc(newGroupAccessibility)}
                />
            </div>
        );
    }

    private onShowMoreOrLessButtonClick = (source: string) => {
        const { scenarioId, userIdentity } = this.props;
        const { userCalendarGroups, groupCalendarGroups } = getCalendarGroupsForUser(userIdentity);
        // If the account is collapsed, expand it
        if (isAccountCollapsed(scenarioId, userIdentity)) {
            expandAccount(scenarioId, userIdentity);
        }
        // Clear any unselected calendars from condensed view when Show more/less is pressed within the scenario
        setScenarioCondensedViewCalendars(getSelectedCalendars(), scenarioId, userIdentity);
        toggleCondenseCalendarAccount(
            [...userCalendarGroups, ...groupCalendarGroups],
            scenarioId,
            userIdentity,
            source
        );
    };

    private onShowMoreOrLessButtonClickFromPickerMenu = () => {
        this.onShowMoreOrLessButtonClick('calendarPicker');
    };

    private onShowMoreOrLessButtonClickFromContextMenu = () => {
        this.onShowMoreOrLessButtonClick('accountContextMenu');
    };

    private onNewCalendarGroupEntry = (value: string) => {
        createCalendarGroup(this.props.scenarioId, this.props.userIdentity, value);
    };

    private onStoppedCreatingCalendarGroup = () => {
        stoppedCreatingCalendarGroup(this.props.scenarioId);
    };

    /**
     * Context menu handler for creating a calendar group
     */
    private onNewCalendarGroup = () => {
        let source = 'accountContextMenu';
        logUsage('newCalendarGroupClicked', { source_1: source });
        const { scenarioId, userIdentity } = this.props;
        expandAccount(scenarioId, userIdentity);
        if (isAccountCondensed(scenarioId, userIdentity)) {
            const {
                userCalendarGroups,
                groupCalendarGroups,
                teamsCalendarGroups,
            } = getCalendarGroupsForUser(userIdentity);
            toggleCondenseCalendarAccount(
                [...userCalendarGroups, ...groupCalendarGroups, ...teamsCalendarGroups],
                scenarioId,
                userIdentity,
                source
            );
        }
        startedCreatingCalendarGroup(scenarioId, userIdentity);
    };

    /**
     * Context menu handler for deleting calendar group
     */
    private onDeleteCalendarGroup = () => {
        const groups = getCalendarGroups();
        if (groups.length > 0 && !!this.calendarPickerGroupRefs[groups[0].calendarGroupId.id]) {
            this.calendarPickerGroupRefs[groups[0].calendarGroupId.id].focus();
        }
    };

    private onCalendarGroupHeaderClick = () => {
        toggleCalendarAccount(this.props.scenarioId, this.props.userIdentity);
    };

    /**
     * Account header handler for on key down
     */
    private accountHeaderOnKeyDown = (ev: React.KeyboardEvent<unknown>) => {
        switch (ev.which) {
            case KeyCodes.right:
                ev.stopPropagation();
                this.contextMenuButtonRef.focus();
                break;
        }
    };

    private contextMenuOnKeyDown = (ev: React.KeyboardEvent<unknown>) => {
        switch (ev.which) {
            case KeyCodes.left:
                ev.stopPropagation();
                this.buttonRef.focus();
                break;
        }
    };

    /**
     * Context menu handler for calendar account
     */
    private onContextMenuClick = (ev: React.MouseEvent<HTMLButtonElement>) => {
        ev.stopPropagation();

        this.onContextMenu(ev);
    };

    private onContextMenu = (ev: React.MouseEvent<HTMLButtonElement>) => {
        ev.preventDefault();

        this.setState({ contextMenuIsActive: true });
        showCalendarContextMenu(
            this.props.scenarioId,
            this.props.userIdentity,
            getAnchorElementForContextMenu(ev)
        );
    };

    private onContextMenuDismiss = () => {
        hideContextMenu(this.props.scenarioId);
        this.setState({ contextMenuIsActive: false });
    };

    private setButtonRef = (button: IButton) => {
        this.buttonRef = button;
    };

    private setContextMenuButtonRef = (button: IButton) => {
        this.contextMenuButtonRef = button;
    };

    private contextMenuButtonOnFocus = () => {
        this.setState({ contextMenuHasKeyboardFocus: true });
    };

    private contextMenuButtonOnBlur = () => {
        this.setState({ contextMenuHasKeyboardFocus: false });
    };
}

/**
 * Determines whether to show the More button for group calendars
 */
function getShowMoreForGroupCalendars(userIdentity: string) {
    // We only want to show the More button if there are any hidden calendars
    const groupId = getCalendarGroupKey(userIdentity, GROUP_CALENDAR_GROUP_ID);
    const groupCalendars = getCalendarsForCalendarGroup(
        userIdentity,
        {
            calendarGroupId: {
                id: groupId,
                mailboxInfo: {
                    type: 'GroupMailbox',
                    userIdentity: userIdentity,
                    mailboxSmtpAddress: userIdentity,
                },
            },
            serverGroupId: groupId,
            GroupName: groupId,
            GroupType: CalendarGroupType.Normal,
        },
        GetCalendarsListType.All
    );
    const topAndSelectedGroupCalendars = getTopAndSelectedCalendars(groupCalendars);
    return (
        groupCalendars.length > 0 &&
        groupCalendars.length > MAX_CALENDARS_FOR_TOP_CALENDARS_LIST &&
        groupCalendars.length > topAndSelectedGroupCalendars.length
    );
}

function showAccountLoadError(userIdentity: string, accountProvider: AccountProviderType) {
    const state = getOWAConnectedAccountStatus(userIdentity);
    accountIsInErrorState(state, accountProvider);
}
